// src/validators/price-table.validator.ts
import { BRAZILIAN_UF_VALUES } from "@bcube/shared/enums";
function validatePriceTableConditions(groups) {
  const totalConds = groups.reduce((acc, g) => acc + (g.conditions?.length ?? 0), 0);
  if (totalConds === 0) {
    return {
      status: "red",
      messages: [
        "Nenhuma condi\xE7\xE3o foi configurada, favor configurar ao menos uma condi\xE7\xE3o antes de realizar a valida\xE7\xE3o!"
      ]
    };
  }
  const problems = [];
  if (hasInvalidBillingLimit(groups)) {
    problems.push("Informe um limite de faturamento v\xE1lido.");
  }
  const groupIds = /* @__PURE__ */ new Set();
  for (const group of groups) {
    if (group.id !== void 0) {
      if (!group.id.trim() || groupIds.has(group.id)) {
        problems.push("IDs de grupos devem ser preenchidos e \xFAnicos na tabela.");
      }
      groupIds.add(group.id);
    }
    const conditionIds = /* @__PURE__ */ new Set();
    for (const condition of group.conditions ?? []) {
      if (!condition.id.trim() || conditionIds.has(condition.id)) {
        problems.push("IDs de condi\xE7\xF5es devem ser preenchidos e \xFAnicos dentro de cada grupo.");
      }
      conditionIds.add(condition.id);
    }
  }
  if (groups.some((g) => (g.conditions?.length ?? 0) === 0)) {
    problems.push("Cada grupo deve conter ao menos uma condi\xE7\xE3o.");
  }
  const cov = validateCoverage(groups);
  if (!cov.ok) {
    const msg = cov.missing.length ? `\xC9 obrigat\xF3rio que todos os estados estejam inclu\xEDdos em condi\xE7\xF5es (ou uma condi\xE7\xE3o com "Vendas para" vazio cobrindo os demais estados). Faltando: ${cov.missing.join(", ")}.` : '\xC9 obrigat\xF3rio que todos os estados estejam inclu\xEDdos em condi\xE7\xF5es (ou uma condi\xE7\xE3o com "Vendas para" vazio cobrindo os demais estados).';
    problems.push(msg);
  }
  problems.push(...validateGroupGeography(groups));
  problems.push(...validateIcmsConsistency(groups));
  problems.push(...validateIcmsCoverage(groups));
  problems.push(...validateStateFallbacks(groups));
  const comp = validateCompanyRequired(groups);
  if (!comp.ok) {
    problems.push("Em cada condi\xE7\xE3o \xE9 obrigat\xF3rio faturar para uma empresa.");
  }
  const dups = findDuplicates(groups);
  if (dups.length > 0) {
    problems.push("N\xE3o \xE9 poss\xEDvel cadastrar condi\xE7\xF5es duplicadas.");
  }
  const prio = validatePriorityGroups(groups);
  if (!prio.ok) {
    problems.push(
      "Para habilitar a prioridade de faturamento de um grupo \xE9 necess\xE1rio conter limite de faturamento em todas as condi\xE7\xF5es do grupo, com exce\xE7\xE3o da \xFAltima que dever\xE1 estar sem limite cadastrado."
    );
  }
  if (problems.length > 0) {
    return { status: "yellow", messages: problems };
  }
  return { status: "green", messages: ["Condi\xE7\xF5es validadas com sucesso!"] };
}
function parseNumberPTBR(value) {
  if (value === void 0 || value === null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = value.trim().replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function hasInvalidBillingLimit(groups) {
  return groups.some(
    (group) => (group.conditions ?? []).some((condition) => {
      const limit = condition.billingLimitInCents;
      if (typeof limit === "string" && limit.trim() === "") return false;
      return limit !== void 0 && parseNumberPTBR(limit) === null;
    })
  );
}
function isUnlimited(limit) {
  const n = parseNumberPTBR(limit);
  return !n || n <= 0;
}
function isALL(salesFor) {
  return !salesFor || salesFor.length === 0;
}
function isActive(c) {
  return !!c?.toBillFor?.trim();
}
function icmsCategory(icms) {
  if (icms === true) return "SIM";
  if (icms === false) return "NAO";
  return "NONE";
}
function icmsLabel(category) {
  if (category === "SIM") return "Sim";
  if (category === "NAO") return "N\xE3o";
  return "";
}
function groupMode(conditions) {
  const active = (conditions ?? []).filter(isActive);
  const categories = new Set(active.map((c) => icmsCategory(c.icmsContributor)));
  const hasNone = categories.has("NONE");
  const hasSegmented = categories.has("SIM") || categories.has("NAO");
  if (hasNone && hasSegmented) return "MIXED";
  if (hasSegmented) return "ICMS";
  return "NONE";
}
function limitKey(limit) {
  const n = parseNumberPTBR(limit);
  if (!n || n <= 0) return "UNL";
  return String(n);
}
function statesKey(salesFor) {
  if (isALL(salesFor)) return "ALL";
  const arr = Array.from(new Set(salesFor ?? []));
  arr.sort();
  return arr.join("|");
}
function billKey(toBillFor) {
  return (toBillFor ?? "").trim().toUpperCase();
}
function categoryStates(conditions) {
  const set = /* @__PURE__ */ new Set();
  conditions.forEach((c) => {
    (c.salesFor ?? []).forEach((uf) => {
      set.add(uf);
    });
  });
  return set;
}
function validateCoverage(groups) {
  const conditions = groups.flatMap((g) => g.conditions ?? []);
  if (conditions.some((c) => isALL(c.salesFor))) {
    return { ok: true, missing: [] };
  }
  const covered = /* @__PURE__ */ new Set();
  for (const c of conditions) {
    (c.salesFor ?? []).forEach((uf) => {
      covered.add(uf);
    });
  }
  const missing = BRAZILIAN_UF_VALUES.filter((uf) => !covered.has(uf));
  return { ok: missing.length === 0, missing };
}
function validateGroupGeography(groups) {
  const explicitGroupsByState = /* @__PURE__ */ new Map();
  const genericGroups = [];
  groups.forEach((group, groupIndex) => {
    let hasGenericCondition = false;
    (group.conditions ?? []).forEach((condition) => {
      if (isALL(condition.salesFor)) {
        hasGenericCondition = true;
        return;
      }
      new Set(condition.salesFor).forEach((uf) => {
        const groupIndexes = explicitGroupsByState.get(uf) ?? /* @__PURE__ */ new Set();
        groupIndexes.add(groupIndex);
        explicitGroupsByState.set(uf, groupIndexes);
      });
    });
    if (hasGenericCondition) genericGroups.push(groupIndex);
  });
  const messages = [];
  explicitGroupsByState.forEach((groupIndexes, uf) => {
    if (groupIndexes.size < 2) return;
    const labels = [...groupIndexes].sort((a, b) => a - b).map((index) => index + 1);
    messages.push(
      `A UF ${uf} est\xE1 configurada explicitamente em mais de um grupo (grupos ${labels.join(", ")}).`
    );
  });
  if (genericGroups.length > 1) {
    const labels = genericGroups.map((index) => index + 1);
    messages.push(
      `Existe mais de um grupo com condi\xE7\xF5es gen\xE9ricas (Vendas para vazio), causando concorr\xEAncia entre os grupos ${labels.join(", ")}.`
    );
  }
  return messages;
}
function validateIcmsConsistency(groups) {
  const messages = [];
  groups.forEach((g, gi) => {
    if (groupMode(g.conditions) === "MIXED") {
      messages.push(
        `Grupo ${gi + 1}: n\xE3o \xE9 poss\xEDvel misturar condi\xE7\xF5es com e sem Contribuinte ICMS no mesmo grupo.`
      );
    }
  });
  return messages;
}
function validateIcmsCoverage(groups) {
  const messages = [];
  groups.forEach((g, gi) => {
    if (groupMode(g.conditions) !== "ICMS") return;
    const active = (g.conditions ?? []).filter(isActive);
    const simConditions = active.filter((c) => icmsCategory(c.icmsContributor) === "SIM");
    const naoConditions = active.filter((c) => icmsCategory(c.icmsContributor) === "NAO");
    const simStates = categoryStates(simConditions);
    const naoStates = categoryStates(naoConditions);
    const simGeneric = simConditions.some((c) => isALL(c.salesFor));
    const naoGeneric = naoConditions.some((c) => isALL(c.salesFor));
    const parts = [];
    const missingNao = [...simStates].filter((uf) => !naoStates.has(uf));
    const missingSim = [...naoStates].filter((uf) => !simStates.has(uf));
    if (missingNao.length) parts.push(`sem cobertura 'N\xE3o': ${missingNao.join(", ")}`);
    if (missingSim.length) parts.push(`sem cobertura 'Sim': ${missingSim.join(", ")}`);
    if (simGeneric && !naoGeneric) {
      parts.push("sem cobertura 'N\xE3o': condi\xE7\xE3o gen\xE9rica (Vendas para vazio)");
    }
    if (naoGeneric && !simGeneric) {
      parts.push("sem cobertura 'Sim': condi\xE7\xE3o gen\xE9rica (Vendas para vazio)");
    }
    if (parts.length === 0) return;
    messages.push(`Grupo ${gi + 1}: cobertura de ICMS inconsistente (${parts.join("; ")}).`);
  });
  return messages;
}
function validateStateFallbacks(groups) {
  const messages = [];
  groups.forEach((group, groupIndex) => {
    const active = (group.conditions ?? []).filter(isActive);
    const mode = groupMode(active);
    if (mode === "MIXED") return;
    const categories = mode === "ICMS" ? ["SIM", "NAO"] : ["NONE"];
    categories.forEach((category) => {
      const categoryConditions = active.filter(
        (condition) => icmsCategory(condition.icmsContributor) === category
      );
      const limited = categoryConditions.filter(
        (condition) => !isUnlimited(condition.billingLimitInCents)
      );
      if (limited.length === 0) return;
      const unlimited = categoryConditions.filter(
        (condition) => isUnlimited(condition.billingLimitInCents)
      );
      const hasGenericUnlimited = unlimited.some((condition) => isALL(condition.salesFor));
      const unlimitedStates = categoryStates(unlimited);
      const needsGenericFallback = limited.some((condition) => isALL(condition.salesFor));
      const limitedStates = categoryStates(limited);
      const missingStates = hasGenericUnlimited ? [] : [...limitedStates].filter((uf) => !unlimitedStates.has(uf));
      const missingParts = [];
      if (needsGenericFallback && !hasGenericUnlimited) {
        missingParts.push("condi\xE7\xE3o gen\xE9rica limitada (Vendas para vazio)");
      }
      if (missingStates.length > 0) missingParts.push(missingStates.join(", "));
      if (missingParts.length === 0) return;
      const label = icmsLabel(category);
      const suffix = label ? ` (${label})` : "";
      messages.push(
        `Grupo ${groupIndex + 1}: falta condi\xE7\xE3o de fallback (sem limite)${suffix} no mesmo grupo para: ${missingParts.join("; ")}.`
      );
    });
  });
  return messages;
}
function validateCompanyRequired(groups) {
  const missingSomewhere = groups.some(
    (g) => (g.conditions ?? []).some((c) => !c?.toBillFor?.trim())
  );
  return { ok: !missingSomewhere };
}
function findDuplicates(groups) {
  const map = /* @__PURE__ */ new Map();
  groups.forEach((g) => {
    (g.conditions ?? []).forEach((c) => {
      if (!isActive(c)) return;
      const key = `${statesKey(c.salesFor)}::${billKey(c.toBillFor)}::${limitKey(c.billingLimitInCents)}::${icmsCategory(c.icmsContributor)}`;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
  });
  const duplicates = [];
  map.forEach((count, key) => {
    if (count > 1) duplicates.push(key);
  });
  return duplicates;
}
function validatePriorityGroups(groups) {
  const invalid = [];
  groups.forEach((g, gi) => {
    const priorityEnabled = g.priority === true || g.priorityEnabled === true;
    if (!priorityEnabled) return;
    const list = g.conditions ?? [];
    const mode = groupMode(list);
    if (mode === "MIXED") return;
    if (mode === "NONE") {
      if (list.length < 2) {
        invalid.push(gi);
        return;
      }
      const allButLastHaveLimit = list.slice(0, -1).every((c) => !isUnlimited(c.billingLimitInCents));
      const lastCondition = list[list.length - 1];
      const lastIsUnlimited = lastCondition ? isUnlimited(lastCondition.billingLimitInCents) : false;
      if (!(allButLastHaveLimit && lastIsUnlimited)) invalid.push(gi);
      return;
    }
    const chains = { SIM: [], NAO: [] };
    list.forEach((c) => {
      if (!isActive(c)) return;
      const category = icmsCategory(c.icmsContributor);
      if (category === "SIM") chains.SIM.push(c);
      else if (category === "NAO") chains.NAO.push(c);
    });
    const invalidChain = ["SIM", "NAO"].some((category) => {
      const chain = chains[category];
      if (chain.length === 0) return false;
      const allButLastHaveLimit = chain.slice(0, -1).every((c) => !isUnlimited(c.billingLimitInCents));
      const last = chain[chain.length - 1];
      const lastIsUnlimited = last ? isUnlimited(last.billingLimitInCents) : false;
      return !(allButLastHaveLimit && lastIsUnlimited);
    });
    if (invalidChain) invalid.push(gi);
  });
  return { ok: invalid.length === 0, invalid };
}
function selectApplicableBillingGroup(groups, state) {
  const explicit = groups.filter((group2) => group2.conditions.some((c) => !c.generic && c.states.some((uf) => uf.trim().toUpperCase() === state)));
  if (explicit.length > 1) return { success: false, code: "MULTIPLE_EXPLICIT_GROUPS" };
  const generic = groups.filter((group2) => group2.conditions.some((c) => c.generic));
  if (!explicit.length && generic.length > 1) return { success: false, code: "MULTIPLE_GENERIC_GROUPS" };
  const group = explicit[0] ?? generic[0];
  return group ? { success: true, group, explicit: explicit.length > 0 } : { success: false, code: "NO_APPLICABLE_GROUP" };
}
export {
  selectApplicableBillingGroup,
  validatePriceTableConditions
};
//# sourceMappingURL=index.js.map